import { useState, useEffect } from "react";
import { Button, Form, Modal, Spinner, Badge } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";

const API = "https://dailydish.in/api/production/cookable-items";

const TYPE_LABEL = { weight: "Weight (kg)", pieces: "Pieces (pcs)", pack: "Pack" };
const TYPE_COLOR = { weight: "#185FA5", pieces: "#085041", pack: "#BA7517" };
const TYPE_BG    = { weight: "#E6F1FB",  pieces: "#E1F5EE",  pack: "#FAEEDA" };

function ItemRow({ item, onEdit, onDelete }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "14px 20px", borderBottom: "1px solid #f0f0f0",
      background: "#fff", transition: "background 0.15s",
    }}
      onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
      onMouseLeave={e => e.currentTarget.style.background = "#fff"}
    >
      <div className="d-flex align-items-center gap-3">
        <div style={{
          width: 38, height: 38, borderRadius: 10, display: "flex",
          alignItems: "center", justifyContent: "center", fontSize: 18,
          background: TYPE_BG[item.outputType] || "#f0f0f0",
        }}>
          {item.outputType === "weight" ? "⚖️" : item.outputType === "pieces" ? "🍽️" : "📦"}
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{item.name}</div>
          <span style={{
            fontSize: 12, fontWeight: 500, padding: "2px 10px", borderRadius: 20,
            background: TYPE_BG[item.outputType], color: TYPE_COLOR[item.outputType],
          }}>
            {TYPE_LABEL[item.outputType]}
          </span>
        </div>
      </div>
      <div className="d-flex gap-2">
        <Button size="sm" variant="outline-secondary" style={{ borderRadius: 8 }} onClick={() => onEdit(item)}>Edit</Button>
        <Button size="sm" variant="outline-danger" style={{ borderRadius: 8 }} onClick={() => onDelete(item._id)}>Delete</Button>
      </div>
    </div>
  );
}

function ItemModal({ show, onHide, initial, onSave }) {
  const isEdit = !!initial;
  const [name, setName] = useState("");
  const [outputType, setOutputType] = useState("weight");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) { setName(initial?.name || ""); setOutputType(initial?.outputType || "weight"); }
  }, [show, initial]);

  const handleSave = async () => {
    if (!name.trim()) return toast.error("Enter item name");
    setSaving(true);
    try {
      const res = isEdit
        ? await axios.put(`${API}/${initial._id}`, { name: name.trim(), outputType })
        : await axios.post(API, { name: name.trim(), outputType });
      if (res.data.success) { onSave(res.data.data, isEdit); onHide(); }
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton style={{ borderBottom: "1px solid #f0f0f0" }}>
        <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>{isEdit ? "Edit Cookable Item" : "Add Cookable Item"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 500, fontSize: 13, color: "#555" }}>Item name</Form.Label>
          <Form.Control placeholder="e.g. Biryani Rice" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSave()} style={{ borderRadius: 8 }} />
        </Form.Group>
        <Form.Group>
          <Form.Label style={{ fontWeight: 500, fontSize: 13, color: "#555" }}>Output type</Form.Label>
          <div className="d-flex gap-2">
            {Object.entries(TYPE_LABEL).map(([val, label]) => (
              <div key={val} onClick={() => setOutputType(val)} style={{
                flex: 1, padding: "10px 8px", borderRadius: 8, textAlign: "center", cursor: "pointer",
                border: outputType === val ? `2px solid ${TYPE_COLOR[val]}` : "2px solid #e0e0e0",
                background: outputType === val ? TYPE_BG[val] : "#fafafa",
                color: outputType === val ? TYPE_COLOR[val] : "#888",
                fontWeight: 500, fontSize: 13, transition: "all 0.15s",
              }}>
                {val === "weight" ? "⚖️" : val === "pieces" ? "🍽️" : "📦"}<br />{label}
              </div>
            ))}
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "1px solid #f0f0f0" }}>
        <Button variant="light" onClick={onHide} style={{ borderRadius: 8 }}>Cancel</Button>
        <Button variant="success" onClick={handleSave} disabled={saving} style={{ borderRadius: 8 }}>
          {saving ? <Spinner size="sm" /> : isEdit ? "Save Changes" : "Add Item"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function CookableItemsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      if (res.data.success) setItems(res.data.data);
    } catch { toast.error("Failed to load cookable items"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const openAdd = () => { setEditing(null); setShow(true); };
  const openEdit = (item) => { setEditing(item); setShow(true); };

  const handleSave = (saved, isEdit) => {
    if (isEdit) setItems(prev => prev.map(i => i._id === saved._id ? saved : i));
    else setItems(prev => [saved, ...prev]);
    toast.success(isEdit ? "Item updated" : "Item added");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this cookable item?")) return;
    try {
      await axios.delete(`${API}/${id}`);
      setItems(prev => prev.filter(i => i._id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 style={{ fontWeight: 700, marginBottom: 2 }}>Cookable Items</h5>
          <div style={{ fontSize: 13, color: "#888" }}>Raw preparations your kitchen produces</div>
        </div>
        <Button variant="success" onClick={openAdd} style={{ borderRadius: 8, fontWeight: 500 }}>+ Add Item</Button>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #eee", overflow: "hidden" }}>
        {loading ? (
          <div className="text-center py-5"><Spinner /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-5" style={{ color: "#aaa" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🍳</div>
            <div style={{ fontWeight: 500 }}>No cookable items yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Add items like "Biryani Rice", "Dal", "Chapati"</div>
          </div>
        ) : items.map(item => (
          <ItemRow key={item._id} item={item} onEdit={openEdit} onDelete={handleDelete} />
        ))}
      </div>

      <ItemModal show={show} onHide={() => setShow(false)} initial={editing} onSave={handleSave} />
    </>
  );
}
